import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NbDialogRef, NbDialogService } from '@nebular/theme';
import { Observable, Subject, filter } from 'rxjs';

import { CourseSyncBulkStatus, LmsRegistryService } from './lms-registry.service';
import { MoodleService } from './moodle.service';
import {
  OrbiDistributeDialogComponent,
  OrbiDistributeResult,
} from '../components/dialogs/orbi-distribute-dialog/orbi-distribute-dialog.component';

/** Ein laufender Bulk-Kurs-Sync (`POST /lms/placements/course-sync/bulk`). */
interface TrackedCourseSyncJob {
  jobId: string;
  /** Vor dem Serverjob gescheiterte Kurse (Placement anlegen) — zählen im
   *  Ergebnis als fehlgeschlagen, der Server kennt sie nicht. */
  preparationFailedCount: number;
  startedAt: number;
}

interface TrackerEntry {
  job: TrackedCourseSyncJob;
  dialogRef: NbDialogRef<OrbiDistributeDialogComponent> | null;
  timer: ReturnType<typeof setTimeout> | null;
  retryDelayMs: number;
  /** Abgemeldet (401): wartet auf den nächsten `resume()`. */
  paused: boolean;
}

export interface CourseSyncJobUpdate {
  jobId: string;
  /** Letzter Serverstand; `null` nur, wenn der Job nicht (mehr) abrufbar ist. */
  status: CourseSyncBulkStatus | null;
  finished: boolean;
  result?: OrbiDistributeResult;
}

/** Offene Jobs überleben Neuladen und Schließen des Portals: beim nächsten
 *  Öffnen (`resume()` im Dashboard-Layout) wird weiter abgefragt und das
 *  Ergebnis-Modal nachgeholt. */
const STORAGE_KEY = 'al-course-sync-jobs-v1';
/** Ältere Einträge sind sicher tot (Server-Job längst final oder verworfen). */
const MAX_JOB_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const POLL_INTERVAL_MS = 3000;
/** Netzfehler (Laptop im Ruhezustand, WLAN weg) sind kein fehlgeschlagener
 *  Sync — es wird mit wachsendem Abstand weiter gefragt. */
const MAX_RETRY_DELAY_MS = 30000;
/** Beim Wiederaufnehmen kurz warten, bis `nb-layout` und damit der
 *  Overlay-Container für das Ergebnis-Modal steht. */
const RESUME_DELAY_MS = 1500;

/**
 * Verfolgt Bulk-Kurs-Syncs portalweit — unabhängig davon, ob die
 * Platzierungen-Seite noch offen ist.
 *
 * Der Server arbeitet ab der `job_id` allein weiter; dieser Dienst fragt nur
 * den Stand ab (alle 3 s, wie vorher die Seite) und meldet das Ergebnis:
 * - ist das Verteil-Modal noch offen, landet es dort (Schwarm bleibt stehen,
 *   Haken bzw. Status erscheint);
 * - wurde es mit „Weiterarbeiten" geschlossen, öffnet sich ein neues
 *   Ergebnis-Modal — auf welcher Portalseite der Nutzer auch gerade ist, und
 *   es bleibt stehen, bis er es schließt. Ein Toast ginge in einem anderen
 *   Tab oder beim Kaffeeholen unter (Marcel, 26.09.2026).
 *
 * Die Platzierungen-Seite hört über `updates(jobId)` mit (Fortschrittsbalken,
 * Zeilenstatus, Toast), solange sie offen ist.
 */
@Injectable({ providedIn: 'root' })
export class CourseSyncJobTrackerService {
  private readonly entries = new Map<string, TrackerEntry>();
  private readonly updatesSubject = new Subject<CourseSyncJobUpdate>();

  constructor(
    private lms: LmsRegistryService,
    private moodle: MoodleService,
    private dialog: NbDialogService,
  ) {}

  updates(jobId: string): Observable<CourseSyncJobUpdate> {
    return this.updatesSubject.pipe(filter((update) => update.jobId === jobId));
  }

  /** Übernimmt einen frisch gestarteten Job. Ein übergebenes Verteil-Modal
   *  bekommt ab jetzt den Hinweis „läuft im Hintergrund" samt
   *  „Weiterarbeiten"-Knopf. */
  track(
    job: { jobId: string; preparationFailedCount: number },
    dialogRef: NbDialogRef<OrbiDistributeDialogComponent> | null,
  ): void {
    const entry = this.register({ ...job, startedAt: Date.now() });
    this.persist();
    if (dialogRef) this.attachDialog(entry, dialogRef);
    this.schedule(entry, POLL_INTERVAL_MS);
  }

  /** Nimmt gespeicherte Jobs nach Neuladen / erneutem Öffnen / erneutem
   *  Login wieder auf. Mehrfach aufrufen ist harmlos. */
  resume(): void {
    for (const job of this.readStorage()) {
      if (this.entries.has(job.jobId)) continue;
      if (Date.now() - job.startedAt > MAX_JOB_AGE_MS) continue;
      const entry = this.register(job);
      this.schedule(entry, RESUME_DELAY_MS);
    }
    for (const entry of this.entries.values()) {
      if (!entry.paused) continue;
      entry.paused = false;
      this.schedule(entry, RESUME_DELAY_MS);
    }
    this.persist();
  }

  private register(job: TrackedCourseSyncJob): TrackerEntry {
    const existing = this.entries.get(job.jobId);
    if (existing) return existing;
    const entry: TrackerEntry = { job, dialogRef: null, timer: null, retryDelayMs: POLL_INTERVAL_MS, paused: false };
    this.entries.set(job.jobId, entry);
    return entry;
  }

  private attachDialog(entry: TrackerEntry, ref: NbDialogRef<OrbiDistributeDialogComponent>): void {
    entry.dialogRef = ref;
    ref.componentRef?.instance?.allowBackground();
    ref.onClose.subscribe(() => {
      if (entry.dialogRef === ref) entry.dialogRef = null;
    });
  }

  private schedule(entry: TrackerEntry, delayMs: number): void {
    if (entry.timer) clearTimeout(entry.timer);
    entry.timer = setTimeout(() => {
      entry.timer = null;
      this.poll(entry);
    }, delayMs);
  }

  private poll(entry: TrackerEntry): void {
    if (!this.entries.has(entry.job.jobId) || entry.paused) return;
    this.lms.getCourseSyncBulkStatus(entry.job.jobId).subscribe({
      next: (status) => {
        entry.retryDelayMs = POLL_INTERVAL_MS;
        if (status.status === 'COMPLETED' || status.status === 'FAILED') {
          const successCount = status.completed_courses || 0;
          const failedCount = entry.job.preparationFailedCount + (status.failed_courses || 0);
          this.finish(entry, status, successCount, failedCount);
          return;
        }
        this.updatesSubject.next({ jobId: entry.job.jobId, status, finished: false });
        this.schedule(entry, POLL_INTERVAL_MS);
      },
      error: (err: HttpErrorResponse) => {
        // Abgemeldet: nicht weiter fragen, aber gespeichert lassen — nach
        // dem nächsten Login holt `resume()` den Job wieder ab.
        if (err?.status === 401) {
          entry.paused = true;
          return;
        }
        // Job unbekannt (anderer Mandant, verworfen): nicht mehr zu retten.
        if (err?.status === 403 || err?.status === 404) {
          this.finish(entry, null, 0, entry.job.preparationFailedCount || 1, false);
          return;
        }
        entry.retryDelayMs = Math.min(entry.retryDelayMs * 2, MAX_RETRY_DELAY_MS);
        this.schedule(entry, entry.retryDelayMs);
      },
    });
  }

  private finish(
    entry: TrackerEntry,
    status: CourseSyncBulkStatus | null,
    successCount: number,
    failedCount: number,
    showDialog = true,
  ): void {
    if (entry.timer) clearTimeout(entry.timer);
    this.entries.delete(entry.job.jobId);
    this.persist();

    const outcome = successCount === 0 ? 'failed' : failedCount > 0 ? 'partial' : 'success';
    // Der Grund des ersten gescheiterten Kurses — in der Praxis scheitern alle
    // Kurse einer Moodle-Instanz am selben (Token, Service-Nutzer, Rechte).
    const reason = this.moodle.translateCourseSyncError(status?.course_errors?.[0]) ?? undefined;
    const result: OrbiDistributeResult = { outcome, successCount, failedCount, reason };

    if (entry.dialogRef) {
      entry.dialogRef.componentRef?.instance?.complete(result);
    } else if (showDialog) {
      this.dialog.open(OrbiDistributeDialogComponent, {
        context: { mode: 'sync', initialResult: result },
        hasBackdrop: true,
        closeOnBackdropClick: false,
        closeOnEsc: false,
      });
    }

    this.updatesSubject.next({ jobId: entry.job.jobId, status, finished: true, result });
  }

  // --- localStorage ------------------------------------------------------
  // Kann in privaten Fenstern oder bei gesperrtem Speicher werfen — dann
  // funktioniert alles weiter, nur ohne Wiederaufnahme nach dem Neuladen.

  private readStorage(): TrackedCourseSyncJob[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed)
        ? parsed.filter((job) => typeof job?.jobId === 'string' && typeof job?.startedAt === 'number')
        : [];
    } catch {
      return [];
    }
  }

  private persist(): void {
    try {
      const jobs = Array.from(this.entries.values()).map((entry) => entry.job);
      if (jobs.length) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(jobs));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // siehe oben
    }
  }
}
